import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, OnChanges, SimpleChanges, TemplateRef, inject, input, output, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';
import { Breakpoint, BreakpointState, MediaQueryService } from '../../services/media-query.service';
import { ButtonComponent } from '../../components/button/button.component';
import { CalendarStyleType, DateRange } from '../../components/date-range-selector/date-range-selector.domain';
import { DividerComponent } from '../../components/panel/divider.component';
import { FormComponent } from '../../components/form/form.component';
import { FormRowComponent } from '../../components/form/row/form-row.component';
import { InputDirective } from '../../components/input/input.directive';
import { InputErrorsComponent } from '../../components/form/input-errors/input-errors.component';
import { InputGroupComponent } from '../../components/form/input-group/input-group.component';
import { InputGroupFieldComponent } from '../../components/form/input-group/input-group-field.component';
import { InputGroupLabelComponent } from '../../components/form/input-group/input-group-label.component';
import { InputGroupSuffixComponent } from '../../components/form/input-group/input-group-suffix.component';
import { RangeSelectorCalendarComponent } from '../../components/date-range-selector/range-selector-calendar/range-selector-calendar.component';
import { RangeSelectorCalendarInputDirective } from '../../components/date-range-selector/range-selector-calendar/range-selector-calendar-input.directive';

@Component({
  selector: 'td-wb-date-range-picker',
  templateUrl: 'date-range-picker.component.html',
  styleUrls: ['date-range-picker.component.scss'],
  imports: [
    RangeSelectorCalendarComponent,
    NgTemplateOutlet,
    FormComponent,
    FormRowComponent,
    FormsModule,
    RangeSelectorCalendarInputDirective,
    InputDirective,
    InputErrorsComponent,
    InputGroupComponent,
    InputGroupFieldComponent,
    InputGroupLabelComponent,
    InputGroupSuffixComponent,
    ButtonComponent,
    DividerComponent,
    TranslateDirective
  ]
})
export class DateRangePickerComponent implements OnChanges {
  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly startDateInput = input<Date>(undefined);
  readonly endDateInput = input<Date>(undefined);
  readonly minCustomDate = input<Date>(undefined);
  readonly maxCustomDate = input<Date>(undefined);

  readonly customRangeSelected = output<DateRange>();

  readonly picker = viewChild<RangeSelectorCalendarComponent>('picker');

  startDate: Date;
  endDate: Date;

  get content(): TemplateRef<unknown> {
    const picker = this.picker();
    return picker === undefined ? undefined : picker.content();
  }

  calendarStyle: CalendarStyleType = 'regular';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startDateInput']) {
      this.startDate = this.startDateInput();
    }
    if (changes['endDateInput']) {
      this.endDate = this.endDateInput();
    }
  }

  constructor() {
    this.mediaQueryService.observe(Breakpoint.SM)
      .pipe(takeUntilDestroyed())
      .subscribe((breakpointState: BreakpointState): void => {
        this.calendarStyle = breakpointState.isBreakpointMatch(Breakpoint.SM) ? 'compact' : 'regular';
        this.changeDetectorRef.markForCheck();
      });
  }

  onCustomRangeSubmit(): void {
    this.picker().close();
    const customRange = { startDate: this.startDate, endDate: this.endDate };
    this.customRangeSelected.emit(customRange);
  }
}
